import { createSlice } from '@reduxjs/toolkit';

import {
    fetchStudent360Data,
    uploadIEPEvaluationPDF,
    generateIEPWithAI,
    generateAccommodationPlanAI,
    generateSessionSubjectPlanAI,
    saveIEPData,
} from './AIIEP/aiIepThunks';

const initialState = {
    student360Data: null,
    /** Student id currently shown in AIIEP dashboard — used to ignore stale responses */
    activeStudentId: null,
    is360Loading: false,
    isUploadingPDF: false,
    isGeneratingAccommodation: false,
    isGeneratingSessionSubject: false,
    isSaving: false,
    error: null,
};

function resolveStudentId(value) {
    if (!value) return null;
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (value._id) return String(value._id);
    return String(value);
}

function isActiveStudent(state, studentId) {
    if (!studentId || !state.activeStudentId) return false;
    return String(state.activeStudentId) === String(studentId);
}

const iEP360Slice = createSlice({
    name: 'iep360',
    initialState,
    reducers: {
        clearStudent360(state) {
            state.student360Data = null;
            state.activeStudentId = null;
            state.is360Loading = false;
            state.isUploadingPDF = false;
            state.isGeneratingAccommodation = false;
            state.isGeneratingSessionSubject = false;
            state.isSaving = false;
            state.error = null;
        },
        setActiveIepStudent(state, action) {
            state.activeStudentId = resolveStudentId(action.payload);
            state.student360Data = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchStudent360Data.pending, (state, action) => {
                const requestStudentId = resolveStudentId(action.meta?.arg?.studentId);
                const switched = Boolean(
                    state.activeStudentId
                    && requestStudentId
                    && String(state.activeStudentId) !== String(requestStudentId),
                );
                state.activeStudentId = requestStudentId;
                state.is360Loading = true;
                state.error = null;
                // Same student refresh must keep current profile (avoids Unknown Student flash)
                if (switched || !state.student360Data) {
                    state.student360Data = null;
                }
            })
            .addCase(fetchStudent360Data.fulfilled, (state, action) => {
                const requestStudentId = resolveStudentId(action.meta?.arg?.studentId);
                if (!isActiveStudent(state, requestStudentId)) return;

                state.is360Loading = false;
                state.student360Data = action.payload;
            })
            .addCase(fetchStudent360Data.rejected, (state, action) => {
                const requestStudentId = resolveStudentId(action.meta?.arg?.studentId);
                if (!isActiveStudent(state, requestStudentId)) return;

                state.is360Loading = false;
                state.error = action.payload?.message || action.error?.message || 'Failed to fetch Student 360 profile';
            });

        builder
            .addCase(uploadIEPEvaluationPDF.pending, (state) => {
                state.isUploadingPDF = true;
                state.error = null;
            })
            .addCase(uploadIEPEvaluationPDF.fulfilled, (state, action) => {
                state.isUploadingPDF = false;
                const requestStudentId = resolveStudentId(action.meta?.arg?.studentId);
                if (!isActiveStudent(state, requestStudentId) || !state.student360Data) return;

                state.student360Data.iepData = {
                    ...state.student360Data.iepData,
                    evaluationDetails: action.payload.evaluationDetails || action.payload.evaluationData,
                    clinicalEvaluationSource: 'pdf',
                    clinicalEvaluationReviewed: false,
                };
            })
            .addCase(uploadIEPEvaluationPDF.rejected, (state, action) => {
                state.isUploadingPDF = false;
                state.error = action.payload?.message || action.error?.message || 'Failed to analyze PDF';
            });

        builder
            .addCase(generateIEPWithAI.pending, (state) => {
                state.is360Loading = true;
                state.error = null;
            })
            .addCase(generateIEPWithAI.fulfilled, (state, action) => {
                state.is360Loading = false;
                const requestStudentId = resolveStudentId(action.meta?.arg?.studentId);
                // Never merge previous student's AI result into the currently open student
                if (!isActiveStudent(state, requestStudentId) || !state.student360Data) return;

                const payload = action.payload || {};
                state.student360Data.iepData = {
                    ...state.student360Data.iepData,
                    ...payload,
                    evaluationDetails: payload.clinicalEvaluationSource === 'pdf'
                        ? (payload.evaluationDetails || state.student360Data.iepData?.evaluationDetails)
                        : {},
                    clinicalEvaluationSource: payload.clinicalEvaluationSource || '',
                    clinicalEvaluationReviewed: payload.clinicalEvaluationReviewed === true,
                };
            })
            .addCase(generateIEPWithAI.rejected, (state, action) => {
                const requestStudentId = resolveStudentId(action.meta?.arg?.studentId);
                if (!isActiveStudent(state, requestStudentId)) return;

                state.is360Loading = false;
                state.error = action.payload?.message || action.error?.message || 'Failed to generate IEP';
            });

        builder
            .addCase(saveIEPData.pending, (state) => {
                state.isSaving = true;
                state.error = null;
            })
            .addCase(saveIEPData.fulfilled, (state, action) => {
                state.isSaving = false;
                const requestStudentId = resolveStudentId(action.meta?.arg?.studentId);
                if (!isActiveStudent(state, requestStudentId) || !state.student360Data) return;

                state.student360Data.iepData = {
                    ...state.student360Data.iepData,
                    ...action.payload,
                };
            })
            .addCase(saveIEPData.rejected, (state, action) => {
                state.isSaving = false;
                state.error = action.payload?.message || action.error?.message || 'Failed to save IEP Data';
            });

        builder
            .addCase(generateAccommodationPlanAI.pending, (state) => {
                state.isGeneratingAccommodation = true;
                state.error = null;
            })
            .addCase(generateAccommodationPlanAI.fulfilled, (state, action) => {
                state.isGeneratingAccommodation = false;
                const requestStudentId = resolveStudentId(action.meta?.arg?.studentId);
                if (!isActiveStudent(state, requestStudentId) || !state.student360Data) return;

                const plan = action.payload?.data?.accommodationPlan
                    || action.payload?.accommodationPlan
                    || action.payload?.data;
                state.student360Data.iepData = {
                    ...state.student360Data.iepData,
                    accommodationPlan: plan?.accommodationPlan || plan,
                };
            })
            .addCase(generateAccommodationPlanAI.rejected, (state, action) => {
                state.isGeneratingAccommodation = false;
                state.error = action.payload?.message || action.error?.message || 'Failed to generate accommodation plan';
            });

        builder
            .addCase(generateSessionSubjectPlanAI.pending, (state) => {
                state.isGeneratingSessionSubject = true;
                state.error = null;
            })
            .addCase(generateSessionSubjectPlanAI.fulfilled, (state, action) => {
                state.isGeneratingSessionSubject = false;
                const requestStudentId = resolveStudentId(action.meta?.arg?.studentId);
                if (!isActiveStudent(state, requestStudentId) || !state.student360Data) return;

                if (action.payload?.data) {
                    state.student360Data.iepData = {
                        ...state.student360Data.iepData,
                        sessionPlan: action.payload.data.sessionPlan,
                        subjectAdaptations: action.payload.data.subjectAdaptations,
                    };
                }
            })
            .addCase(generateSessionSubjectPlanAI.rejected, (state, action) => {
                state.isGeneratingSessionSubject = false;
                state.error = typeof action.payload === 'string'
                    ? action.payload
                    : (action.payload?.message || action.error?.message || 'Failed to generate session plan');
            });
    },
});

export const { clearStudent360, setActiveIepStudent } = iEP360Slice.actions;
export default iEP360Slice.reducer;